import { useDrawerEscape } from '../../lib/hooks/useDrawerEscape';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Stili per le animazioni
const animationStyles = `
  @keyframes modalFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes modalFadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes modalSlideOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(10px);
    }
  }

  .modal-backdrop-enter {
    animation: modalFadeIn 0.2s ease-out forwards;
  }

  .modal-backdrop-exit {
    animation: modalFadeOut 0.2s ease-in forwards;
  }

  .modal-content-enter {
    animation: modalSlideIn 0.25s ease-out forwards;
  }

  .modal-content-exit {
    animation: modalSlideOut 0.2s ease-in forwards;
  }
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide' | 'fullscreen';
  closeable?: boolean;
  variant?: 'default' | 'glass';
  headerRight?: React.ReactNode;
}

export function Modal({ isOpen, onClose, title, children, size = 'md', closeable = true, variant = 'default', headerRight }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { if(closeTimer.current) clearTimeout(closeTimer.current); setIsClosing(false); return () => { if(closeTimer.current) clearTimeout(closeTimer.current); }; }, [isOpen]);

  // Gestione chiusura con animazione
  const handleClose = useCallback(() => {
    if (!closeable) return;
    if(closeTimer.current) clearTimeout(closeTimer.current);
    setIsClosing(true);
    closeTimer.current = setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200); // Durata animazione di uscita
  }, [closeable, onClose]);

  useDrawerEscape(isOpen,()=>{if(closeable)handleClose();},20000);

  // Creiamo il modal root solo una volta quando il componente viene montato
  useEffect(() => {
    const root = document.getElementById('modal-root') || (() => {
      const el = document.createElement('div');
      el.id = 'modal-root';
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.left = '0';
      el.style.width = '100%';
      el.style.height = '100%';
      el.style.zIndex = '9999';
      document.body.appendChild(el);
      return el;
    })();

    setModalRoot(root);

    return () => {
      // Non facciamo nulla qui, perché vogliamo mantenere il nodo #modal-root
    };
  }, []);

  // Gestione render basata su isOpen
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    }
  }, [isOpen]);

  // Effetto per gestire l'apertura e la chiusura del modale
  useEffect(() => {
    if (!isOpen || !modalRoot) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeable) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose, modalRoot, closeable]);

  // Se il modale non deve essere renderizzato
  if ((!isOpen && !isClosing) || !modalRoot) {
    if (shouldRender && !isOpen && !isClosing) {
      setShouldRender(false);
    }
    return null;
  }

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    wide: 'w-[98vw]',
    fullscreen: 'w-[calc(100vw-2rem)] sm:w-[calc(100vw-3rem)]'
  };

  const isWideOrFullscreen = size === 'fullscreen' || size === 'wide';

  const getVariantClasses = (isFullscreen: boolean) => {
    if (isFullscreen) {
      // Fullscreen: sfondo solido Notion
      return {
        default: 'bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] shadow-xl',
        glass: 'bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] shadow-xl'
      };
    }
    // Normal modals: Notion solid background
    return {
      default: 'bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] shadow-xl',
      glass: 'bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] shadow-xl'
    };
  };

  const variantClasses = getVariantClasses(isWideOrFullscreen);

  const modalContent = (
    <>
      {/* Inietta gli stili per le animazioni */}
      <style>{animationStyles}</style>

      <div
        className={`fixed inset-0 flex items-center justify-center z-[20000] bg-black/50 ${
          isClosing ? 'modal-backdrop-exit' : 'modal-backdrop-enter'
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget && closeable) {
            handleClose();
          }
        }}
      >
        <div className={`modal-container flex items-center justify-center ${isWideOrFullscreen ? 'h-full' : 'min-h-screen'} ${isWideOrFullscreen ? 'p-0' : 'px-4 py-0'}`}>
          <div
            ref={modalRef}
            className={`text-left shadow-2xl sm:align-middle ${isWideOrFullscreen ? 'w-[98vw] h-[98vh]' : `sm:w-full ${sizeClasses[size]} max-h-[90vh] sm:my-8`} ${isWideOrFullscreen ? 'rounded-2xl' : 'rounded-xl'} flex flex-col ${variantClasses[variant]} relative transition-all ${isClosing ? 'modal-content-exit' : 'modal-content-enter'} overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >

            {/* In modalità wide/fullscreen mostriamo solo una X floating in alto a destra
                così il modale resta chiudibile anche quando il contenuto non fornisce un proprio header.
                Rispetta closeable={false} (es. wizard a step obbligati). */}
            {isWideOrFullscreen && closeable && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="absolute top-3 right-3 z-20 p-2 rounded-full bg-white/90 dark:bg-[#2F3437]/90 hover:bg-gray-100 dark:hover:bg-gray-700 text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white transition-colors focus:outline-none shadow-sm backdrop-blur-sm border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Header nascosto per fullscreen/wide - il contenuto ha il suo header */}
            {!isWideOrFullscreen && (
              <div className="px-4 py-3 flex-shrink-0 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-[#37352F] dark:text-white">{title}</h3>
                  <div className="flex items-center gap-3">
                    {headerRight}
                    {closeable && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClose();
                        }}
                        className="text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white transition-colors focus:outline-none"
                        aria-label="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className={`flex-1 ${isWideOrFullscreen ? 'overflow-hidden h-full' : 'overflow-y-auto px-4 pb-4 sm:px-6'}`}>
              <div className={isWideOrFullscreen ? 'h-full' : 'mt-2'}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, modalRoot);
}